import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { OwnerLayout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { Progress } from '../../components/ui/progress';
import { Trophy, Star, ChevronRight, CheckCircle, Play, Lock, Package, Award, Download, PawPrint } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ProgressRing({ percent, size = 96, stroke = 8, color = '#7C9A7E' }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        cx={size / 2} cy={size / 2} r={radius}
        fill="none" stroke="#f3f4f6" strokeWidth={stroke}
      />
      <circle
        cx={size / 2} cy={size / 2} r={radius}
        fill="none" stroke={color} strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="transition-all duration-700 ease-out"
      />
    </svg>
  );
}

function DogProgressCard({ dog }) {
  const pct = dog.progress_percent || 0;
  const ringColor = pct >= 75 ? '#7C9A7E' : pct >= 40 ? '#F59E0B' : '#2D3B47';
  return (
    <div
      data-testid={`dog-progress-card-${dog.id}`}
      className="bg-white rounded-2xl border-2 border-gray-100 shadow-sm p-5 flex flex-col items-center gap-3 hover:border-edupaws-sage/40 transition-all"
    >
      <div className="relative">
        <ProgressRing percent={pct} color={ringColor} />
        <div className="absolute inset-0 flex items-center justify-center">
          {dog.photo_url ? (
            <img src={dog.photo_url} alt={dog.name} className="w-16 h-16 rounded-full object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-edupaws-sage/10 flex items-center justify-center">
              <PawPrint className="w-7 h-7 text-edupaws-sage" />
            </div>
          )}
        </div>
      </div>
      <div className="text-center">
        <h3 className="font-heading font-bold text-gray-800 text-lg">{dog.name}</h3>
        {dog.breed && <p className="text-xs text-gray-400 font-body">{dog.breed}</p>}
      </div>
      <div className="text-center">
        <span className="font-heading text-2xl font-bold" style={{ color: ringColor }}>{pct}%</span>
        <p className="text-xs text-gray-500 font-body">{dog.completed_videos}/{dog.total_videos} videos</p>
      </div>
    </div>
  );
}

const PHASE_COLORS = {
  "Phase 1: Foundation": "bg-edupaws-sage",
  "Phase 2: Application": "bg-edupaws-navy",
  "Phase 3: Transformation": "bg-amber-500",
};

export default function OwnerDashboard() {
  const { user, activeDog } = useAuth();
  const [boxes, setBoxes] = useState([]);
  const [videos, setVideos] = useState({});
  const [progress, setProgress] = useState({});
  const [badges, setBadges] = useState([]);
  const [badgeDefs, setBadgeDefs] = useState([]);
  const [dogProgress, setDogProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [activeDog]);

  const fetchData = async () => {
    if (!activeDog) {
      setLoading(false);
      return;
    }

    try {
      const [seriesRes, progressRes, badgesRes, badgeDefsRes, dogProgressRes] = await Promise.all([
        axios.get(`${API}/series`),
        axios.get(`${API}/progress/${activeDog.id}`),
        axios.get(`${API}/badges/${activeDog.id}`),
        axios.get(`${API}/badges/definitions`),
        axios.get(`${API}/dogs/owner/${user.id}/progress-summary`)
      ]);

      const boxesData = seriesRes.data;
      setBoxes(boxesData);

      const videoMap = {};
      for (const box of boxesData) {
        const vidsRes = await axios.get(`${API}/videos/series/${box.id}`);
        videoMap[box.id] = vidsRes.data;
      }
      setVideos(videoMap);

      const progressMap = {};
      progressRes.data.forEach(p => {
        progressMap[p.video_id] = p;
      });
      setProgress(progressMap);
      setBadges(badgesRes.data || []);
      setBadgeDefs(badgeDefsRes.data || []);
      setDogProgress(dogProgressRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBoxStatus = (box) => {
    const boxVids = videos[box.id] || [];
    if (boxVids.length === 0) return 'locked';
    const vid = boxVids[0];
    const prog = progress[vid.id];
    return prog?.status || 'locked';
  };

  // Only count training boxes (not Welcome/Bonus)
  const trainingBoxes = boxes.filter(b => b.order_index > 0);
  const completedCount = trainingBoxes.filter(b => getBoxStatus(b) === 'complete').length;
  const overallPercent = trainingBoxes.length > 0 ? Math.round((completedCount / trainingBoxes.length) * 100) : 0;

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-edupaws-sage border-t-transparent"></div>
        </div>
      </OwnerLayout>
    );
  }

  if (!activeDog) {
    return (
      <OwnerLayout>
        <div className="text-center py-16">
          <div className="w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Star className="w-10 h-10 text-orange-400" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-gray-800 mb-4">
            No Dog Selected
          </h2>
          <p className="text-gray-500 font-body mb-6">
            Add your first dog to start tracking progress
          </p>
          <Link
            to="/owner/dogs"
            data-testid="add-dog-btn"
            className="inline-block font-heading font-bold py-3 px-8 rounded-full text-white
              bg-edupaws-navy shadow-[0_4px_0_#3C4753] hover:shadow-[0_2px_0_#3C4753] hover:translate-y-[2px] transition-all"
          >
            Add Your Dog
          </Link>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout>
      {/* Welcome Section */}
      <div className="mb-10">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-800 mb-2">
          Welcome back, {user?.name?.split(' ')[0]}!
        </h1>
        <p className="text-gray-500 font-body text-lg">
          Training with <span className="text-edupaws-sage font-medium">{activeDog.name}</span>, Let's continue the journey!
        </p>
      </div>

      {/* Progress Overview */}
      <div className="grid md:grid-cols-3 gap-6 mb-12">
        <div className="bg-white rounded-3xl p-6 border-2 border-gray-100 shadow-sm" data-testid="overall-progress">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-edupaws-sage/20 rounded-xl flex items-center justify-center">
              <Trophy className="w-6 h-6 text-edupaws-sage" />
            </div>
            <div>
              <div className="font-heading text-3xl font-bold text-gray-800">{overallPercent}%</div>
              <div className="text-sm text-gray-500 font-body">Overall Progress</div>
            </div>
          </div>
          <Progress value={overallPercent} className="h-3" />
        </div>

        <div className="bg-white rounded-3xl p-6 border-2 border-gray-100 shadow-sm" data-testid="boxes-completed">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <div className="font-heading text-3xl font-bold text-gray-800">{completedCount}</div>
              <div className="text-sm text-gray-500 font-body">Boxes Completed</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl p-6 border-2 border-gray-100 shadow-sm" data-testid="boxes-remaining">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
              <Play className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <div className="font-heading text-3xl font-bold text-gray-800">
                {trainingBoxes.length - completedCount}
              </div>
              <div className="text-sm text-gray-500 font-body">Boxes Remaining</div>
            </div>
          </div>
        </div>
      </div>

      {/* Dog Progress Cards */}
      {dogProgress.length > 0 && (
        <div className="mb-12" data-testid="dog-progress-section">
          <h2 className="font-heading text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <PawPrint className="w-6 h-6 text-edupaws-sage" />
            Dog Progress
          </h2>
          <div className={`grid gap-6 ${dogProgress.length === 1 ? 'md:grid-cols-1 max-w-xs' : dogProgress.length === 2 ? 'md:grid-cols-2 max-w-lg' : 'md:grid-cols-3'}`}>
            {dogProgress.map(dog => (
              <DogProgressCard key={dog.id} dog={dog} />
            ))}
          </div>
        </div>
      )}

      {/* Achievement Badges */}
      <div className="mb-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Award className="w-6 h-6 text-amber-500" />
            Achievement Badges
          </h2>
          <span className="text-sm font-body text-gray-500">{badges.length}/10 earned</span>
        </div>
        <div className="grid grid-cols-5 sm:grid-cols-10 gap-3 mb-4">
          {badgeDefs.map((def) => {
            const earned = badges.find(b => b.box_index === def.box_index);
            return (
              <div
                key={def.box_index}
                data-testid={`badge-${def.box_index}`}
                className={`relative flex flex-col items-center p-3 rounded-2xl border-2 transition-all ${
                  earned
                    ? 'bg-amber-50 border-amber-300 shadow-sm'
                    : 'bg-gray-50 border-gray-200 opacity-40'
                }`}
                title={earned ? `${def.name} - Earned!` : `${def.name} - Locked`}
              >
                <span className="text-2xl mb-1">{def.icon}</span>
                <span className="text-[10px] font-body font-bold text-center leading-tight text-gray-600">
                  Box {def.box_index}
                </span>
                {earned && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {badges.length >= 10 && (
          <button
            data-testid="download-certificate-btn"
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/certificates/${activeDog.id}`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a');
                a.href = url;
                a.download = `Edu_Paws_Certificate_${activeDog.name}.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
                toast.success('Certificate downloaded!');
              } catch (e) {
                toast.error('Could not download certificate');
              }
            }}
            className="flex items-center gap-2 bg-amber-500 text-white px-6 py-3 rounded-full font-heading font-bold
              shadow-[0_4px_0_#b45309] hover:shadow-[0_2px_0_#b45309] hover:translate-y-[2px] transition-all"
          >
            <Download className="w-5 h-5" />
            Download Graduation Certificate
          </button>
        )}
      </div>

      {/* 10-Month Journey */}
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-bold text-gray-800 mb-6">
          Your 10-Month Journey
        </h2>
      </div>

      <div className="space-y-3">
        {boxes.map((box, idx) => {
          const status = getBoxStatus(box);
          const isComplete = status === 'complete';
          const isReady = status === 'unlocked';
          const isLocked = status === 'locked';
          const phaseColor = PHASE_COLORS[box.phase] || 'bg-gray-400';

          return (
            <Link
              key={box.id}
              to="/owner/series"
              data-testid={`journey-box-${box.order_index}`}
              className={`group flex items-center gap-4 bg-white rounded-2xl p-4 border-2 transition-all hover:shadow-md
                ${isComplete ? 'border-edupaws-sage/40' : isReady ? 'border-amber-200' : 'border-gray-100'}`}
            >
              {/* Status Icon */}
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0
                ${isComplete ? 'bg-edupaws-sage/20' : isReady ? 'bg-amber-50' : 'bg-gray-100'}`}>
                {isComplete ? (
                  <CheckCircle className="w-6 h-6 text-edupaws-sage" />
                ) : isReady ? (
                  <Play className="w-6 h-6 text-amber-500" />
                ) : (
                  <Lock className="w-5 h-5 text-gray-300" />
                )}
              </div>

              {/* Box Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <div className={`w-2 h-2 rounded-full ${phaseColor}`} />
                  <span className="text-xs text-gray-400 font-body">{box.phase}</span>
                </div>
                <h3 className="font-heading font-bold text-gray-800 group-hover:text-edupaws-sage transition-colors truncate">
                  {box.title}
                </h3>
                <p className="text-sm text-gray-500 font-body truncate">{box.description}</p>
              </div>

              {/* Status Badge */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`px-3 py-1 rounded-full text-xs font-body font-bold
                  ${isComplete ? 'bg-edupaws-sage/20 text-edupaws-navy' : isReady ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-400'}`}>
                  {isComplete ? 'Complete' : isReady ? 'Ready' : `Month ${box.order_index}`}
                </span>
                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-edupaws-sage group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
          );
        })}
      </div>
    </OwnerLayout>
  );
}
